import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { Session } from '@obraplan/core/application/session';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import type { Container } from '@/app/container';
import { lotesPorDisciplina } from '@/features/planning/frente-pela-disciplina';
import { useContainer, useSessionState, useVisiblePages } from '@/features/session/use-session';

/** Quantas peças de frente pedir de uma vez: um dia de obra tem dezenas de pares frente × serviço. */
const LOTE = 4;

/**
 * Quantos dias antes de hoje a Conferência fica no aparelho (RN-636): a semana que passou, para
 * conferir sem sinal o dia que ficou para trás. Sem as fotos — só as já vistas ficam (decisão do
 * Gilvan, 29/10) — e sem as pranchas, que pesam e são as de hoje.
 */
export const DIAS_ANTERIORES_NO_APARELHO = 7;

/**
 * O que esta página já baixou: `pessoa|obra|dia`. A pré-carga roda uma vez por dia e obra, e não
 * a cada volta do sinal — no canteiro o sinal oscila, e cada volta baixaria de novo as pranchas
 * (centenas de KB a MB cada) pelos dados do tablet. A tela aberta já refaz as leituras dela na
 * volta (`retryWhileOffline`).
 */
const jaBaixados = new Set<string>();

/**
 * Baixa a Conferência de hoje da obra aberta, com sinal, sem ninguém pedir (Fase 43, RN-600), e a
 * dos `DIAS_ANTERIORES_NO_APARELHO` dias antes dela (RN-636).
 *
 * É o D2 da entrevista: quem sobe a obra não precisa lembrar de abrir nada antes. As leituras
 * são **as mesmas** que a tela faz, com os mesmos argumentos — é isso que faz a tela, sem sinal,
 * achar no aparelho o que esta função deixou lá (`offline-cache.ts`).
 *
 * Roda uma vez por dia e obra, assim que houver sinal. Quem não abre a Conferência não baixa nada.
 */
export function usePreloadToday(): void {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const abreConferencia = useVisiblePages().some((page) => page.path === '/conferencia');
  const { connection } = container;
  const online = useSyncExternalStore(connection.subscribe, () => connection.snapshot().online);

  // A sessão muda de identidade a cada troca de obra; quem decide rodar de novo é o usuário.
  const sessao = useRef(session);
  sessao.current = session;

  useEffect(() => {
    const atual = sessao.current;
    if (atual === undefined || activeProjectId === undefined || !abreConferencia || !online) {
      return undefined;
    }
    const chave = `${atual.userId}|${activeProjectId}|${container.clock.today('America/Sao_Paulo').toString()}`;
    if (jaBaixados.has(chave)) return undefined;
    let vivo = true;
    void preloadToday(container, atual, activeProjectId, () => vivo).then(
      () => {
        // Só conta como baixado o que terminou: a que parou no meio (trocou de obra) tenta de novo.
        if (vivo) jaBaixados.add(chave);
      },
      () => undefined,
    );
    return () => {
      vivo = false;
    };
  }, [container, session?.userId, activeProjectId, abreConferencia, online]);
}

/**
 * As leituras da Conferência de hoje e dos dias anteriores. Exportada para o teste conferir que
 * batem com as da tela.
 *
 * Hoje primeiro, inteiro, com as pranchas: é o dia que mais se usa. Depois de ontem para trás, um
 * dia por vez; a troca de obra no meio para tudo, e o dia que não terminou é baixado de novo na
 * próxima vez.
 */
export async function preloadToday(
  container: Container,
  session: Session,
  projectId: ProjectId,
  still: () => boolean,
): Promise<void> {
  const hoje = container.clock.today('America/Sao_Paulo');

  // O que não muda com o dia: uma leitura só.
  await Promise.all([
    container.getProject.execute(projectId, session),
    // A autoria do histórico. Quem não administra recebe recusa, e a tela já conta com isso.
    container.listUsers.execute({ includeInactive: true }, session),
    // A aba Não conformidades, para encerrar com a solução sem sinal (RN-623).
    container.listProjectNonConformities.execute({ projectId }, session),
    // O efetivo da obra: o "Lançar trabalho" escolhe a pessoa dele.
    container.getWorkforce.execute(projectId, session),
  ]);
  if (!still()) return;

  const plantas = await preloadDia(container, session, projectId, hoje.toString(), still);
  if (plantas === undefined) return;
  await preloadPranchas(container, session, projectId, plantas, still);

  for (let atras = 1; atras <= DIAS_ANTERIORES_NO_APARELHO; atras += 1) {
    if (!still()) return;
    await preloadDia(container, session, projectId, hoje.subtractDays(atras).toString(), still);
  }
}

/**
 * Um dia da Conferência: a pessoa, a equipe, o RDO, a qualidade com as fichas e, por frente ×
 * serviço, as peças e os cômodos. Devolve as plantas das frentes do dia, ou `undefined` quando
 * parou no meio.
 */
async function preloadDia(
  container: Container,
  session: Session,
  projectId: ProjectId,
  date: string,
  still: () => boolean,
): Promise<ReadonlySet<string> | undefined> {
  const doDia = { projectId, date };

  const [, , , , , qualidade] = await Promise.all([
    container.getDailyInspections.execute(doDia, session),
    // O RDO do dia (etapa 3): o documento, as tarefas e o efetivo, como o painel os lê.
    container.getDailyReport.execute(doDia, session),
    container.getDailyReportTasks.execute(doDia, session),
    container.getDailyReportCrew.execute(doDia, session),
    container.getDayTeamMeasurements.execute(doDia, session),
    container.getDayQuality.execute(doDia, session),
  ]);
  if (!still() || !qualidade.ok) return undefined;

  // As fichas de cada disciplina, numa frente dela: sem sinal, o "Lançar trabalho" da atividade
  // que ninguém abriu usa as da disciplina (RN-618). A mesma leitura que a seção faz.
  for (const lote of lotesPorDisciplina(qualidade.value.projectFronts)) {
    if (!still()) return undefined;
    await container.getDayQuality.execute({ ...doDia, activityIds: lote }, session);
  }
  if (!still()) return undefined;

  // Cada frente × serviço do dia: as peças, para a lista do "medir" (PecasDoDia), e os cômodos,
  // para a execução por cômodo (etapa 4). As mesmas leituras da `ExecucaoDaFrente`.
  const pares = qualidade.value.fronts.flatMap((front) =>
    front.services.map((service) => ({
      activityId: front.activityId,
      serviceId: service.serviceId,
    })),
  );
  const plantas = new Set<string>();
  for (let inicio = 0; inicio < pares.length; inicio += LOTE) {
    if (!still()) return undefined;
    await Promise.all(
      pares.slice(inicio, inicio + LOTE).map(async (par) => {
        const [pecas] = await Promise.all([
          container.getFrontPieces.execute({ projectId, ...par, date }, session),
          container.getFrontRooms.execute({ projectId, ...par }, session),
        ]);
        if (pecas.ok && pecas.value.floorPlanId !== undefined) plantas.add(pecas.value.floorPlanId);
      }),
    );
  }

  return plantas;
}

/**
 * A lista da tela de Execução e a prancha de cada pavimento das frentes de hoje (RN-613). A
 * prancha vem inteira na resposta (um data URI), e é ela que pesa: só as de hoje.
 */
async function preloadPranchas(
  container: Container,
  session: Session,
  projectId: ProjectId,
  plantas: ReadonlySet<string>,
  still: () => boolean,
): Promise<void> {
  if (!still()) return;
  const [, pranchas] = await Promise.all([
    container.listScheduleItems.execute({ projectId }, session),
    container.getProjectFloorPlanSheets.execute(projectId, session),
  ]);
  if (!pranchas.ok) return;
  for (const prancha of pranchas.value) {
    if (!still()) return;
    if (prancha.floorPlanId === undefined || !plantas.has(prancha.floorPlanId)) continue;
    await container.getFloorPlanSheetFile.execute({ projectId, sheetId: prancha.id }, session);
  }
}
